/**
 * Family API endpoints - members, vehicles, pets, invitations
 */
import { fetchApi } from './apiBase';
import { FamilyMember, Family, Vehicle, Pet } from '../types';

// Family Invitation type
export interface FamilyInvitation {
  id: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired';
  created_at: string;
  expires_at: string;
}

// === Family ===

export async function getFamily(): Promise<Family> {
  return fetchApi<Family>('/auth/family/');
}

export async function updateFamily(data: { name?: string; home_city?: string; home_state?: string }): Promise<Family> {
  return fetchApi<Family>('/auth/family/', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// === Family Members ===

export async function getFamilyMembers(): Promise<{ members: FamilyMember[] }> {
  return fetchApi<{ members: FamilyMember[] }>('/auth/family/members/');
}

export async function createFamilyMember(data: {
  name: string;
  role: string;
  birth_date?: string;
  school_name?: string;
  school_location?: string;
  school_calendar_url?: string;
  school_start_time?: string;
  school_end_time?: string;
  school_days?: string[];
  aftercare_enabled?: boolean;
  aftercare_end_time?: string;
  aftercare_days?: string[];
  employer_name?: string;
  work_location?: string;
  work_start_time?: string;
  work_end_time?: string;
  work_days?: string[];
  work_is_remote?: boolean;
  work_is_flexible?: boolean;
}): Promise<FamilyMember> {
  return fetchApi<FamilyMember>('/auth/family/members/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFamilyMember(
  id: number,
  data: Partial<FamilyMember>
): Promise<FamilyMember> {
  return fetchApi<FamilyMember>(`/auth/family/members/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteFamilyMember(id: number): Promise<void> {
  await fetchApi(`/auth/family/members/${id}/`, { method: 'DELETE' });
}

// === Family Invitations ===

export async function inviteToFamily(email: string, role: string = 'member'): Promise<{ message: string }> {
  return fetchApi('/auth/family/invite/', {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  });
}

export async function getFamilyInvitations(): Promise<{ invitations: FamilyInvitation[] }> {
  return fetchApi<{ invitations: FamilyInvitation[] }>('/auth/family/invitations/');
}

export async function cancelInvitation(id: string): Promise<{ message: string }> {
  return fetchApi(`/auth/family/invitations/${id}/cancel/`, {
    method: 'POST',
  });
}

export async function resendInvitation(id: string): Promise<{ message: string }> {
  return fetchApi(`/auth/family/invitations/${id}/resend/`, {
    method: 'POST',
  });
}

// === Vehicles ===

export async function getVehicles(): Promise<{ vehicles: Vehicle[] }> {
  return fetchApi<{ vehicles: Vehicle[] }>('/auth/family/vehicles/');
}

export async function createVehicle(data: {
  name: string;
  seats: number;
  primary_driver?: number;
  has_car_seats?: boolean;
  notes?: string;
}): Promise<Vehicle> {
  return fetchApi<Vehicle>('/auth/family/vehicles/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateVehicle(id: number, data: Partial<Vehicle>): Promise<Vehicle> {
  return fetchApi<Vehicle>(`/auth/family/vehicles/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteVehicle(id: number): Promise<void> {
  await fetchApi(`/auth/family/vehicles/${id}/`, { method: 'DELETE' });
}

// === Pets ===

export async function getPets(): Promise<{ pets: Pet[] }> {
  return fetchApi<{ pets: Pet[] }>('/auth/family/pets/');
}

export async function createPet(data: {
  name: string;
  pet_type: 'dog' | 'cat' | 'other';
  needs_walks?: boolean;
  feeding_times?: string[];
  notes?: string;
}): Promise<Pet> {
  return fetchApi<Pet>('/auth/family/pets/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePet(id: number, data: Partial<Pet>): Promise<Pet> {
  return fetchApi<Pet>(`/auth/family/pets/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deletePet(id: number): Promise<void> {
  await fetchApi(`/auth/family/pets/${id}/`, { method: 'DELETE' });
}

// === School Calendar ===

export interface SchoolCalendarLookupResult {
  school_name: string;
  school_id: string;
  calendar_year: string;
  breaks: Array<{
    name: string;
    start_date: string;
    end_date: string;
  }>;
}

export async function lookupSchoolCalendar(
  schoolName: string,
  location?: string
): Promise<SchoolCalendarLookupResult> {
  return fetchApi<SchoolCalendarLookupResult>('/auth/school-calendars/lookup/', {
    method: 'POST',
    body: JSON.stringify({ school_name: schoolName, location }),
  });
}

export async function saveSchoolCalendar(
  memberId: number,
  schoolData: SchoolCalendarLookupResult
): Promise<{ success: boolean }> {
  return fetchApi('/auth/school-calendars/save/', {
    method: 'POST',
    body: JSON.stringify({
      member_id: memberId,
      school_name: schoolData.school_name,
      school_id: schoolData.school_id,
      calendar_year: schoolData.calendar_year,
    }),
  });
}
